import React, { useContext, useEffect, useState } from 'react';

import { ZeosApiContext } from '../../context/ZeosApiContext';
import { IPlaceAddressComponent } from '../../services/zeos-api/contracts/Location';
import { ISimpleListItem, ZeosTypeAhead } from './ZeosTypeAhead';

export interface IAddressLookup {
  StreetAddress: string;
  StreetName: string;
  Neighborhood: string; //SubLocal2
  City: string; //SubLocal1
  County: string; //AdminArea2
  State: string; //AdminArea3
  Country: string; //Country
  PostalCode: string; //Postal
  PostalCodeSuffix: string; //postal_code_suffix
}

const defaultAddress: IAddressLookup = {
  StreetAddress: "",
  StreetName: "",
  Neighborhood: "",
  City: "",
  County: "",
  State: "",
  Country: "",
  PostalCode: "",
  PostalCodeSuffix: "",
};

type GoogleAddressType =
  | "street_number"
  | "route"
  | "neighborhood"
  | "sublocality_level_1"
  | "locality"
  | "administrative_area_level_2"
  | "administrative_area_level_1"
  | "country"
  | "postal_code"
  | "postal_code_suffix";

const simpleMatch = <T,>(arr: T[], val: T) => arr.findIndex((x) => x == val);

const placeToAddress = (components: IPlaceAddressComponent[]) => {
  const address = { ...defaultAddress };
  components.forEach((x) => {
    const match = (val: GoogleAddressType) =>
      x.types.findIndex((t) => t == val) > -1;
    if (match("street_number")) {
      address.StreetAddress = x.shortText;
    } else if (match("route")) {
      address.StreetName = x.shortText;
    } else if (match("neighborhood")) {
      address.Neighborhood = x.shortText;
    } else if (match("sublocality_level_1") || match("locality")) {
      address.City = x.shortText;
    } else if (match("administrative_area_level_2")) {
      address.County = x.shortText;
    } else if (match("administrative_area_level_1")) {
      address.State = x.shortText;
    } else if (match("country")) {
      address.Country = x.shortText;
    } else if (match("postal_code")) {
      address.PostalCode = x.shortText;
    } else if (match("postal_code_suffix")) {
      address.PostalCodeSuffix = x.shortText;
    }
  });
  return address;
};

interface IProps {
  label: string;
  onSelect: (selected: IAddressLookup) => void;
  placeholder?: string;
  types?: string;
  disabled?: boolean;
}

export const PlacesAutocomplete = (props: IProps) => {
  // Address lookup is proxied through the ZEOS API (Location/PlacesAutocomplete,
  // Location/PlaceDetails), which calls Google's Places API (New) server-side.
  // This replaced a direct browser -> corsproxy.io -> legacy Places API call
  // that broke onboarding (BUG 3839): the free CORS proxy and the deprecated
  // legacy endpoints were both unreliable, and the API key was exposed client-side.
  const zeosApi = useContext(ZeosApiContext);

  const [input, setInput] = useState("");
  const [zeosOptions, setZeosOptions] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [isLoading, setIsLoading] = useState(false); //TODO: Add wait spinner

  useEffect(() => {
    // if the input is empty then we skip the API call
    if (!input.trim()) {
      setZeosOptions([]);
      return;
    }
    //waits 300ms before triggering the API call
    const delay = setTimeout(() => {
      zeosApi.Location.placesAutocompleteAsync(input, props.types)
        .then((response) => {
          const newOpts =
            response.data?.map((p) => ({
              label: p.description,
              id: p.placeId,
            })) || [];
          setZeosOptions(newOpts);
        })
        .catch((err) => console.error("Places autocomplete failed", err));
    }, 300);
    return () => clearTimeout(delay);
  }, [input, props.types]);

  const handleSelected = (option: ISimpleListItem) => {
    zeosApi.Location.placeDetailsAsync(String(option.id))
      .then((response) => {
        const address = placeToAddress(response.data || []);
        props.onSelect(address);
      })
      .catch((err) => console.error("Place details lookup failed", err));
  };

  return (
    <ZeosTypeAhead
      label={props.label}
      options={zeosOptions}
      placeholder={props.placeholder || "Select an option..."}
      onChange={(selected) => {
        if (!selected || selected.length === 0) return;
        const first = selected[0];
        const raw =
          typeof first === "string"
            ? { label: first, id: undefined }
            : (first as { label?: string; id?: string });
        let selectedId = raw.id;
        let selectedLabel = raw.label;

        if (!selectedId && selectedLabel) {
          const match = zeosOptions.find(
            (opt) => opt.label === selectedLabel
          );
          selectedId = match?.id;
          selectedLabel = match?.label ?? selectedLabel;
        }
        if (!selectedId) return;
        const listItem: ISimpleListItem = {
          id: selectedId,
          display: selectedLabel || "",
        };
        handleSelected(listItem);
      }}
      onInputChange={(text) => {
        setInput(text);
      }}
      disabled={props.disabled}
      clearable={true}
      filterBy={() => true}
    />
  );
};
